// A metric id (UUID) as it appears embedded in a compound metric's expression
// string, e.g. "6fb43983-36c2-40f6-aa08-d01ccd44403b / 3a34418a-...".
const METRIC_ID_REGEX =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Last line of defence against the "parse error: Expected EOF" class of bug.
 *
 * A compound aggregation request must include every metric referenced by an
 * expression in its `metrics[]`, otherwise the backend leaves the raw child
 * UUID in the expression and fails to parse the whole request. Consumers should
 * gate on readiness so a partial request is never built — but if one slips
 * through, drop the offending expression (the compound just won't compute)
 * rather than let it poison the request, and log a diagnostic so the gap is
 * visible.
 */
const sanitizeExpressions = ({
  metrics,
  expressions,
}: {
  metrics: MetricInput[];
  expressions: Expression2[];
}): Expression2[] => {
  const metricIds = new Set(metrics.map((m) => m.id));

  return expressions.filter((expression) => {
    const referencedIds = expression.expression.match(METRIC_ID_REGEX) || [];
    const missingIds = referencedIds.filter((id) => !metricIds.has(id));

    if (missingIds.length > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `[aggregation] dropping compound expression "${expression.id}" — it references metric id(s) not present in the request: ${missingIds.join(
          ', ',
        )}. A consumer built a request before its compound inputs finished loading.`,
      );
      return false;
    }

    return true;
  });
};

export default sanitizeExpressions;
